/** Display mapping: colour / grey / false colour, window & level with histogram, contours, overlays. */
import { useEffect, useRef } from 'preact/hooks';
import { computed } from '@preact/signals';
import { COLORMAPS, colormapCss, type ColormapId } from '../../core/colormaps';
import { histogram } from '../../core/stats';
import {
  autoRange,
  compareMode,
  display,
  frames,
  measurement,
  updateSignal,
  windowRange,
  type CompareMode,
  type DisplayMode,
} from '../../app/store';
import { Button, Field, NumberField, NumberInput, Section, Segmented, Select, Toggle } from '../controls';

const hist = computed(() => {
  const m = measurement.value;
  if (!m) return null;
  const [lo, hi] = autoRange.value;
  const pad = (hi - lo) * 0.25 || 1;
  return histogram(m.plane.data, 256, lo - pad, hi + pad);
});

function Histogram() {
  const canvas = useRef<HTMLCanvasElement>(null);
  const h = hist.value;
  const [lo, hi] = windowRange.value;
  const d = display.value;
  const drag = useRef<'lo' | 'hi' | null>(null);
  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const W = c.clientWidth;
    const H = c.clientHeight;
    const dpr = window.devicePixelRatio || 1;
    c.width = W * dpr;
    c.height = H * dpr;
    const g = c.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, W, H);
    if (!h) return;
    let max = 0;
    for (const v of h.counts) max = Math.max(max, v);
    const lg = (v: number) => Math.log1p(v) / Math.log1p(max || 1);
    const bw = W / h.bins;
    for (let i = 0; i < h.bins; i++) {
      const v = h.lo + ((i + 0.5) / h.bins) * (h.hi - h.lo);
      const t = (v - lo) / (hi - lo);
      g.fillStyle =
        d.mode === 'false' ? colormapCss(d.colormap, t, d.bands) : `rgba(200,205,215,${0.35 + 0.5 * Math.min(1, Math.max(0, t))})`;
      const bh = lg(h.counts[i]) * (H - 4);
      g.fillRect(i * bw, H - bh, Math.max(1, bw - 0.2), bh);
    }
    const x = (v: number) => ((v - h.lo) / (h.hi - h.lo)) * W;
    g.fillStyle = 'rgba(0,0,0,0.35)';
    g.fillRect(0, 0, Math.max(0, x(lo)), H);
    g.fillRect(x(hi), 0, W - x(hi), H);
    g.strokeStyle = '#6aa8ff';
    g.lineWidth = 2;
    for (const v of [lo, hi]) {
      g.beginPath();
      g.moveTo(x(v), 0);
      g.lineTo(x(v), H);
      g.stroke();
    }
  });
  const toValue = (e: PointerEvent) => {
    const r = canvas.current!.getBoundingClientRect();
    return h!.lo + ((e.clientX - r.left) / r.width) * (h!.hi - h!.lo);
  };
  return (
    <canvas
      ref={canvas}
      class="histogram"
      data-help="display.histogram"
      onPointerDown={(e) => {
        if (!h) return;
        const v = toValue(e);
        drag.current = Math.abs(v - lo) < Math.abs(v - hi) ? 'lo' : 'hi';
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        updateSignal(display, { auto: false, lo, hi, [drag.current]: v });
      }}
      onPointerMove={(e) => {
        if (!drag.current || !h) return;
        const v = toValue(e);
        updateSignal(display, { auto: false, [drag.current]: v });
      }}
      onPointerUp={() => (drag.current = null)}
    />
  );
}

export function DisplayPanel() {
  const d = display.value;
  const [lo, hi] = windowRange.value;
  const setC = (patch: Partial<typeof d.contours>) => updateSignal(display, { contours: { ...d.contours, ...patch } });
  const hasB = !!frames.B.value;
  return (
    <div class="display-panel">
      <Field label="View" help="display.mode">
        <Segmented<DisplayMode>
          value={d.mode}
          testId="display-mode"
          options={[
            { value: 'color', label: 'Colour' },
            { value: 'gray', label: 'Quantity' },
            { value: 'false', label: 'False colour' },
          ]}
          onChange={(v) => updateSignal(display, { mode: v })}
        />
      </Field>
      <Field label="Image" help="display.compare">
        <Select<CompareMode>
          value={compareMode.value}
          testId="compare-mode"
          options={[
            { value: 'A', label: 'A' },
            { value: 'B', label: 'B', disabled: !hasB },
            { value: 'diff', label: 'A − B (difference)', disabled: !hasB },
            { value: 'ratio', label: 'A / B (ratio)', disabled: !hasB },
            { value: 'pct', label: '(A − B) / B in %', disabled: !hasB },
          ]}
          onChange={(v) => (compareMode.value = v)}
        />
      </Field>
      {d.mode === 'color' ? (
        <NumberField
          label="Exposure"
          help="display.exposure"
          value={d.exposure}
          unit="EV"
          step={0.25}
          min={-20}
          max={20}
          onChange={(v) => updateSignal(display, { exposure: v })}
        />
      ) : (
        <>
          {d.mode === 'false' && (
            <Field label="Colormap" help="display.colormap">
              <Select<ColormapId>
                value={d.colormap}
                options={COLORMAPS.map((c) => ({ value: c.id, label: c.label }))}
                onChange={(v) => updateSignal(display, { colormap: v })}
              />
            </Field>
          )}
          {d.mode === 'false' && (
            <NumberField
              label="Bands"
              help="display.bands"
              value={d.bands}
              integer
              min={0}
              max={64}
              onChange={(v) => updateSignal(display, { bands: v })}
            />
          )}
          <Histogram />
          <div class="window-row" data-help="display.window">
            <NumberInput value={lo} onChange={(v) => updateSignal(display, { auto: false, lo: v, hi })} testId="window-lo" />
            <span class="muted">to</span>
            <NumberInput value={hi} onChange={(v) => updateSignal(display, { auto: false, lo, hi: v })} testId="window-hi" />
            <span class="unit">{measurement.value?.unit}</span>
          </div>
          <div class="inline wrap">
            <Toggle label="Auto" checked={d.auto} help="display.auto" onChange={(v) => updateSignal(display, { auto: v })} />
            <Toggle label="Log scale" checked={d.log} help="display.log" onChange={(v) => updateSignal(display, { log: v })} />
            <Button small help="display.minmax" onClick={() => minMax()}>
              Min–max
            </Button>
            <span class="field-inline" data-help="display.percentile">
              clip
              <NumberInput
                value={d.autoPercentile}
                min={0}
                max={20}
                step={0.1}
                unit="%"
                onChange={(v) => updateSignal(display, { autoPercentile: v, auto: true })}
              />
            </span>
          </div>
        </>
      )}
      <Section title="Overlays" help="display.overlays">
        <div class="toggle-grid">
          <Toggle
            label="Saturation"
            checked={d.showClip}
            help="display.clip"
            testId="show-clip"
            onChange={(v) => updateSignal(display, { showClip: v })}
          />
          <Toggle label="Pixel grid" checked={d.showGrid} help="display.grid" onChange={(v) => updateSignal(display, { showGrid: v })} />
          <Toggle
            label="Pixel values"
            checked={d.showValues}
            help="display.values"
            onChange={(v) => updateSignal(display, { showValues: v })}
          />
          <Toggle
            label="ROI labels"
            checked={d.showLabels}
            help="display.labels"
            onChange={(v) => updateSignal(display, { showLabels: v })}
          />
          <Toggle
            label="Scale bar"
            checked={d.showScaleBar}
            help="display.scalebar"
            onChange={(v) => updateSignal(display, { showScaleBar: v })}
          />
          <Toggle label="Legend" checked={d.showLegend} help="display.legend" onChange={(v) => updateSignal(display, { showLegend: v })} />
        </div>
      </Section>
      <Section title="Iso-contours" defaultOpen={d.contours.enabled} help="display.contours">
        <Toggle
          label="Show contour lines"
          checked={d.contours.enabled}
          testId="contours"
          help="display.contours"
          onChange={(v) => setC({ enabled: v })}
        />
        {d.contours.enabled && (
          <>
            <Field label="Levels" help="display.contourLevels">
              <Segmented
                value={d.contours.auto ? 'auto' : 'manual'}
                options={[
                  { value: 'auto', label: 'Evenly spaced' },
                  { value: 'manual', label: 'Manual list' },
                ]}
                onChange={(v) => setC({ auto: v === 'auto' })}
              />
            </Field>
            {d.contours.auto ? (
              <div class="inline">
                <NumberField
                  label="Count"
                  help="display.contourCount"
                  value={d.contours.count}
                  integer
                  min={1}
                  max={50}
                  onChange={(v) => setC({ count: v })}
                />
                <Toggle
                  label="Log spacing"
                  checked={d.contours.logSpacing}
                  help="display.contourLog"
                  onChange={(v) => setC({ logSpacing: v })}
                />
              </div>
            ) : (
              <Field label="Values" help="display.contourList">
                <input
                  type="text"
                  class="levels-input wide"
                  placeholder="e.g. 100, 200, 500"
                  value={d.contours.levels.join(', ')}
                  onChange={(e) =>
                    setC({
                      levels: (e.target as HTMLInputElement).value
                        .split(/[,;\s]+/)
                        .map(Number)
                        .filter(Number.isFinite),
                    })
                  }
                />
              </Field>
            )}
            <div class="inline">
              <Field label="Colour" help="display.contourColor">
                <input type="color" value={d.contours.color} onInput={(e) => setC({ color: (e.target as HTMLInputElement).value })} />
              </Field>
              <Toggle label="Labels" checked={d.contours.labels} help="display.contourLabels" onChange={(v) => setC({ labels: v })} />
            </div>
          </>
        )}
      </Section>
    </div>
  );
}

function minMax() {
  const m = measurement.value;
  if (!m) return;
  let lo = Infinity;
  let hi = -Infinity;
  for (const v of m.plane.data) {
    if (!Number.isFinite(v)) continue;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (hi > lo) updateSignal(display, { auto: false, lo, hi });
}
